"use client";

import {
  CollectionRow,
  CollectionWithCountAndFeaturedSpecial,
} from "@/data/v2/models/collections";
import { ComedianLandingPageEvents, UserEvents, trackEvent } from "@/analytics";
import { SpecialCard, SpecialCardShimmer } from "../cards/SpecialCard";
import {
  SpecialRow,
  SpecialWithComedianAndUnlocked,
  SpecialWithUnlocked,
  getSpecialsForComedian,
} from "@/data/specials";
import {
  SubscriptionWithComedian,
  getUserSubscriptionsAndTheirComediansClient,
} from "@/data/subscriptions";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shadcn/ui/tabs";
import { cn } from "@punchup/utils/cn";
import { genAbsoluteURL } from "@punchup/utils/genAbsoluteURL";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import BundleCard from "../cards/BundleCard";
import { Comedian } from "@/data/comedians";
import { Database } from "@punchup/data/supabase.types";
import ShareButton from "../common/ShareButton";
import { SupabaseClient } from "@supabase/supabase-js";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import dayjs from "dayjs";
import { getFollowedComedians } from "@/data/follows";
import { getUserCanAccessContent } from "@punchup/data/legacy";
import { getUserPaidSpecials } from "@/data/v2/client/user/specials";
import { logException } from "@/analytics/sentry";
import { useExperiment } from "statsig-react";
import { getCollections } from "@/data/v2/client/public/collections";

export const SpecialsDOMElementId = "videos";

enum SpecialsGalleryTabEnum {
  VIDEOS = "Videos",
  PODCASTS = "Podcasts",
  BUNDLES = "Bundles",
}

type SpecialsTab = SpecialsGalleryTabEnum;

const addSpecialUnlockedStatusForUser = async (
  supabase: SupabaseClient<Database>,
  specials: SpecialRow[],
  loggedIn: boolean
): Promise<SpecialWithUnlocked[] | SpecialWithComedianAndUnlocked[]> => {
  let purchasedSpecialIds: string[] = [];
  let userStripeSubscriptions: SubscriptionWithComedian[] = [];
  let userFollowedComedians: {
    comedian_id: string;
  }[] = [];

  // only if the user is logged in
  if (loggedIn) {
    // retrieve user's followed comedians
    const [
      purchasedSpecialIdsResult,
      userStripeSubscriptionsResult,
      userFollowedComediansResult,
    ] = await Promise.all([
      getUserPaidSpecials(),
      getUserSubscriptionsAndTheirComediansClient(supabase),
      getFollowedComedians({ supabase }),
    ]);

    purchasedSpecialIds = purchasedSpecialIdsResult.map(
      (special) => special.id
    );
    userStripeSubscriptions = userStripeSubscriptionsResult;
    userFollowedComedians = userFollowedComediansResult;
  }

  const userFollowedComedianIds = userFollowedComedians.map(
    (object) => object.comedian_id
  );

  const specialsWithUnlocked = specials.map((special) => {
    const userPurchasedSpecial = purchasedSpecialIds.includes(special.id);
    const isUserSubscribed = userStripeSubscriptions.some(
      (subscription) =>
        subscription.stripe_product?.comedian?.id === special.comedian_id
    );
    const userFollowsComedian = userFollowedComedianIds.includes(
      special.comedian_id
    );

    const unlocked = getUserCanAccessContent({
      special,
      userFollowsComedian,
      userPurchasedSpecial,
      isUserSubscribed,
    });
    return { ...special, unlocked };
  });

  return specialsWithUnlocked;
};

export default function SpecialsGallery({
  comedian,
  currentSpecial,
  featuredSpecialId,
  onDoneLoading,
  isLoggedIn,
}: {
  comedian: Comedian;
  featuredSpecialId?: string | null;
  currentSpecial?: SpecialRow;
  onDoneLoading?: () => void;
  isLoggedIn: boolean;
}) {
  // Number of specials to display initially.
  // Note: if changing this, remember to update md:grid-cols-2 below.
  // Tailwind doesn't let us set this value dynamically.
  const displayCountPageSize = 2;
  const [displayCount, setDisplayCount] = useState(displayCountPageSize);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [specialsToDisplay, setSpecialsToDisplay] = useState<
    SpecialWithUnlocked[] | null
  >(null);
  const [podcastsToDisplay, setPodcastsToDisplay] = useState<
    SpecialWithUnlocked[] | null
  >(null);
  const [bundlesToDisplay, setBundlesToDisplay] = useState<
    CollectionWithCountAndFeaturedSpecial[] | null
  >(null);
  const [bundlePrices, setBundlePrices] = useState<{
    [key: string]: number;
  }>({});
  const [userPaidCollectionIds, setUserPaidCollectionIds] = useState<string[]>(
    []
  );
  const [selectedTab, setSelectedTab] = useState<SpecialsTab>(
    SpecialsGalleryTabEnum.VIDEOS
  );
  const mediaParam = useSearchParams().get("media");
  const router = useRouter();
  const pathname = usePathname();
  const currentParams = useSearchParams();

  const { config: reactionsExperimentConfig } = useExperiment(
    "specials-clap-reactions"
  );
  const clapReactionsEnabled = reactionsExperimentConfig.get("enabled", false);

  const supabase = useMemo(() => {
    return createClientComponentClient<Database>();
  }, []);

  useEffect(() => {
    switch (mediaParam?.toLowerCase()) {
      case "videos":
        setSelectedTab(SpecialsGalleryTabEnum.VIDEOS);
        break;
      case "podcasts":
        setSelectedTab(SpecialsGalleryTabEnum.PODCASTS);
        break;
      case "bundles":
        setSelectedTab(SpecialsGalleryTabEnum.BUNDLES);
        break;
      default:
        setSelectedTab(SpecialsGalleryTabEnum.VIDEOS);
    }
  }, [mediaParam]);

  const getBundles = async (comedianId: string) => {
    try {
      return await getCollections({ comedianId });
    } catch (error) {
      logException(error);
      console.error("Error fetching bundles:", error);
      return [];
    }
  };

  const getBundlePrice = async (bundleId: string) => {
    try {
      const res = await fetch(
        `/api/v2/public/products?collection_id=${bundleId}`
      );
      const data = await res.json();
      return data.data[0].price;
    } catch (error) {
      logException(error);
      console.error("Error fetching bundle price:", error);
    }
  };

  const getPaidUserCollections = useCallback(async () => {
    if (!isLoggedIn) return [];
    try {
      const res = await fetch(
        `/api/v2/user/collections?paid=true&comedian_id=${comedian.id}`
      );
      const data = await res.json();
      return data.data;
    } catch (error) {
      logException(error);
      console.error("Error fetching paid user collections:", error);
    }
  }, [comedian.id, isLoggedIn]);

  useEffect(() => {
    const getSpecials = async () => {
      try {
        const [specials, bundles] = await Promise.all([
          getSpecialsForComedian(supabase, comedian),
          getBundles(comedian.id),
        ]);
        const specialsWithUnlocked = await addSpecialUnlockedStatusForUser(
          supabase,
          specials,
          isLoggedIn
        );

        const specialsMapped = specialsWithUnlocked
          .filter(
            (s) => s.id !== currentSpecial?.id && s.id !== featuredSpecialId
          ) // filter out the special currently being watched, if any.
          // Also filter out the special featured on the comedian's main page, but only the prop featuredSpecialId exists!, which is not always!
          .sort(
            (a, b) =>
              dayjs(b.published_at).valueOf() - dayjs(a.published_at).valueOf()
          );

        setSpecialsToDisplay(
          specialsMapped.filter((special) => !special.requires_subscription)
        );
        setPodcastsToDisplay(
          specialsMapped.filter((special) => special.requires_subscription)
        );
        setBundlesToDisplay(bundles);

        const [userPaidCollections, bundlePrices] = await Promise.all([
          getPaidUserCollections(),
          Promise.all(
            bundles.map(
              async (bundle: CollectionWithCountAndFeaturedSpecial) => {
                if (!bundle.requires_purchase) return { [bundle.id]: 0 };
                const price = await getBundlePrice(bundle.id);
                return { [bundle.id]: price };
              }
            )
          ),
        ]);

        setUserPaidCollectionIds(
          userPaidCollections.map((collection: CollectionRow) => collection.id)
        );
        setBundlePrices(
          bundlePrices.reduce((acc, curr) => ({ ...acc, ...curr }), {})
        );
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching specials:", error);
      }
    };
    if (!specialsToDisplay) {
      getSpecials();
    }
  }, [
    comedian,
    comedian.id,
    currentSpecial?.id,
    featuredSpecialId,
    isLoading,
    displayCount,
    specialsToDisplay,
    supabase,
    isLoggedIn,
    getPaidUserCollections,
  ]);

  const hasSubscriptionContent = useMemo(() => {
    return podcastsToDisplay && podcastsToDisplay.length > 0;
  }, [podcastsToDisplay]);

  const hasRegularContent = useMemo(() => {
    return specialsToDisplay && specialsToDisplay.length > 0;
  }, [specialsToDisplay]);

  const hasBundlesContent = useMemo(() => {
    return bundlesToDisplay && bundlesToDisplay.length > 0;
  }, [bundlesToDisplay]);

  useEffect(() => {
    if (!hasRegularContent && hasSubscriptionContent) {
      setSelectedTab(SpecialsGalleryTabEnum.PODCASTS);
    }
    if (!hasRegularContent && !hasSubscriptionContent && hasBundlesContent) {
      setSelectedTab(SpecialsGalleryTabEnum.BUNDLES);
    }
  }, [hasRegularContent, hasSubscriptionContent, hasBundlesContent]);

  useEffect(() => {
    if (!isLoading) {
      onDoneLoading && onDoneLoading();
    }
  }, [isLoading, onDoneLoading]);

  if (!isLoading && !specialsToDisplay?.length) return null;

  const handleSeeMoreSpecials = () => {
    trackEvent(UserEvents.CLICKED_SEE_MORE_SPECIALS, {
      ComedianId: comedian.id,
    });
    setDisplayCount((prevCount) => prevCount + displayCountPageSize);
  };

  const handleSeeMorePodcasts = () => {
    trackEvent(UserEvents.CLICKED_SEE_MORE_PODCASTS, {
      ComedianId: comedian.id,
    });
    setDisplayCount((prevCount) => prevCount + displayCountPageSize);
  };

  const handleSeeMoreBundles = () => {
    trackEvent(UserEvents.CLICKED_SEE_MORE_BUNDLES, {
      ComedianId: comedian.id,
    });
    setDisplayCount((prevCount) => prevCount + displayCountPageSize);
  };

  const handleVideosTabClick = () => {
    const params = new URLSearchParams(currentParams);
    params.set("media", "videos");
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    trackEvent(ComedianLandingPageEvents.SWITCHED_CONTENT_TYPE, {
      TabText: SpecialsGalleryTabEnum.VIDEOS,
      ComedianId: comedian.id,
    });
    setSelectedTab(SpecialsGalleryTabEnum.VIDEOS);
    setDisplayCount(displayCountPageSize);
  };

  const handlePodcastsTabClick = () => {
    const params = new URLSearchParams(currentParams);
    params.set("media", "podcasts");
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    trackEvent(ComedianLandingPageEvents.SWITCHED_CONTENT_TYPE, {
      TabText: SpecialsGalleryTabEnum.PODCASTS,
      ComedianId: comedian.id,
    });
    setSelectedTab(SpecialsGalleryTabEnum.PODCASTS);
    setDisplayCount(displayCountPageSize);
  };

  const handleBundlesTabClick = () => {
    const params = new URLSearchParams(currentParams);
    params.set("media", "bundles");
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    trackEvent(ComedianLandingPageEvents.SWITCHED_CONTENT_TYPE, {
      TabText: SpecialsGalleryTabEnum.BUNDLES,
      ComedianId: comedian.id,
    });
    setSelectedTab(SpecialsGalleryTabEnum.BUNDLES);
    setDisplayCount(displayCountPageSize);
  };

  if (!hasRegularContent && !hasSubscriptionContent && !hasBundlesContent)
    return null;

  const VideoTabContent = (
    <>
      <div className="grid grid-flow-row grid-cols-1 lg:grid-cols-2 justify-items-start gap-y-4 md:gap-y-10 md:gap-x-6">
        {isLoading
          ? Array.from({ length: displayCountPageSize }, (v, i) => (
              <SpecialCardShimmer key={i} />
            ))
          : specialsToDisplay
              ?.slice(0, displayCount)
              .map((special: SpecialWithUnlocked, i: number) => {
                if (
                  selectedTab === SpecialsGalleryTabEnum.VIDEOS &&
                  !special.requires_subscription
                )
                  return (
                    <SpecialCard
                      location="specialsGalleryVideos"
                      key={special.id}
                      comedian={comedian}
                      special={special}
                      width={411}
                      height={236}
                      mobileWidth={165}
                      mobileHeight={165}
                      useSquareImgOnMobile
                      mobileClassName="flex flex-row justify-items-center gap-x-5 w-full"
                    />
                  );
              })}
      </div>
      {specialsToDisplay && displayCount < specialsToDisplay.length && (
        <button
          className="py-4 w-full mt-4 border btn-outlined border-slate-gray-900 text-slate-gray-900 md:w-[unset]"
          onClick={handleSeeMoreSpecials}
        >
          See more
        </button>
      )}
    </>
  );

  const TitleContent = (
    <div className="flex flex-row items-center justify-between mb-1">
      <h4 className="text-3xl">Content</h4>
      {comedian.display_name ? (
        <ShareButton
          shareData={{
            title: comedian.display_name,
            text: `Check out ${comedian.display_name} on Punchup!`,
            url: genAbsoluteURL(`${comedian.slug}`),
          }}
          location="comedian-page-content-section"
        />
      ) : (
        <div />
      )}
    </div>
  );

  if (hasRegularContent && !hasSubscriptionContent && !hasBundlesContent) {
    return (
      <div
        className={cn(
          "mb-4 px-4 md:px-20 xl:mx-auto max-w-7xl",
          !clapReactionsEnabled && "px-4"
        )}
      >
        {TitleContent}
        <div className="flex flex-row justify-start p-1 mt-1 mb-3">
          <h5
            className={cn(
              "pb-1 md:pb-0 text-xl md:text-lg leading-none transition-[color,border-color,text-decoration-color,font-weight] duration-200 ease-in-out border-b-[3px] border-solid hover:text-primary hover:border-b-primary hover:font-semibold",
              // active styles taken from tab below
              "border-b-primary font-semibold text-primary"
            )}
          >
            Videos
          </h5>
        </div>
        {VideoTabContent}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "mb-4 px-4 md:px-20 xl:mx-auto max-w-7xl",
        !clapReactionsEnabled && "px-4"
      )}
    >
      {TitleContent}
      <Tabs value={selectedTab}>
        <TabsList className="flex flex-row justify-around w-full mb-4 bg-white">
          <TabsTrigger value={SpecialsGalleryTabEnum.VIDEOS} asChild>
            <button
              onClick={handleVideosTabClick}
              className={cn(
                "pb-1 md:pb-0 text-xl md:text-lg leading-none transition-[color,border-color,text-decoration-color,font-weight] duration-200 ease-in-out border-b-[3px] border-solid md:w-28 hover:text-primary hover:border-b-primary hover:font-semibold",
                selectedTab === SpecialsGalleryTabEnum.VIDEOS
                  ? "text-primary border-b-primary font-semibold data-[state=active]:text-primary"
                  : "text-[#A4A6A8] border-b-transparent font-regular data-[state=active]:text-[#A4A6A8]",
                "data-[state=active]:no-underline"
              )}
            >
              {SpecialsGalleryTabEnum.VIDEOS}
            </button>
          </TabsTrigger>
          {hasSubscriptionContent && (
            <TabsTrigger value={SpecialsGalleryTabEnum.PODCASTS} asChild>
              <button
                onClick={handlePodcastsTabClick}
                className={cn(
                  "pb-1 md:pb-0 text-xl md:text-lg leading-none transition-[color,border-color,text-decoration-color,font-weight] duration-200 ease-in-out border-b-[3px] border-solid md:w-28 hover:text-primary hover:border-b-primary hover:font-semibold",
                  selectedTab === SpecialsGalleryTabEnum.PODCASTS
                    ? "text-primary border-b-primary font-semibold data-[state=active]:text-primary"
                    : "text-[#A4A6A8] border-b-transparent font-regular data-[state=active]:text-[#A4A6A8]",
                  "data-[state=active]:no-underline"
                )}
              >
                {SpecialsGalleryTabEnum.PODCASTS}
              </button>
            </TabsTrigger>
          )}
          {hasBundlesContent && (
            <TabsTrigger value={SpecialsGalleryTabEnum.BUNDLES} asChild>
              <button
                onClick={handleBundlesTabClick}
                className={cn(
                  "pb-1 md:pb-0 text-xl md:text-lg leading-none transition-[color,border-color,text-decoration-color,font-weight] duration-200 ease-in-out border-b-[3px] border-solid md:w-28 hover:text-primary hover:border-b-primary hover:font-semibold",
                  selectedTab === SpecialsGalleryTabEnum.BUNDLES
                    ? "text-primary border-b-primary font-semibold data-[state=active]:text-primary"
                    : "text-[#A4A6A8] border-b-transparent font-regular data-[state=active]:text-[#A4A6A8]",
                  "data-[state=active]:no-underline"
                )}
              >
                {SpecialsGalleryTabEnum.BUNDLES}
              </button>
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value={SpecialsGalleryTabEnum.VIDEOS}>
          {VideoTabContent}
        </TabsContent>
        <TabsContent value={SpecialsGalleryTabEnum.PODCASTS}>
          <div className="grid grid-flow-row grid-cols-1 lg:grid-cols-2 justify-items-center gap-y-4 md:gap-y-10 md:gap-x-6">
            {isLoading
              ? Array.from({ length: displayCountPageSize }, (v, i) => (
                  <SpecialCardShimmer key={i} />
                ))
              : podcastsToDisplay
                  ?.slice(0, displayCount)
                  .map((special: SpecialWithUnlocked, i: number) => {
                    if (
                      selectedTab === SpecialsGalleryTabEnum.PODCASTS &&
                      special.requires_subscription
                    )
                      return (
                        <SpecialCard
                          location="specialsGalleryPodcasts"
                          key={special.id}
                          comedian={comedian}
                          special={special}
                          width={411}
                          height={236}
                          mobileWidth={165}
                          mobileHeight={165}
                          useSquareImgOnMobile
                          mobileClassName="flex flex-row justify-items-center gap-x-5 w-full"
                        />
                      );
                  })}
          </div>
          {podcastsToDisplay && displayCount < podcastsToDisplay.length && (
            <button
              className="py-4 w-full mt-4 border btn-outlined border-slate-gray-900 text-slate-gray-900 md:w-[unset]"
              onClick={handleSeeMorePodcasts}
            >
              See more
            </button>
          )}
        </TabsContent>
        <TabsContent value={SpecialsGalleryTabEnum.BUNDLES}>
          <div className="grid grid-flow-row grid-cols-1 lg:grid-cols-2 justify-items-center gap-y-4 md:gap-y-10 md:gap-x-6">
            {isLoading
              ? Array.from({ length: displayCountPageSize }, (v, i) => (
                  <SpecialCardShimmer key={i} />
                ))
              : bundlesToDisplay
                  ?.slice(0, displayCount)
                  .map((bundle: CollectionWithCountAndFeaturedSpecial) => {
                    if (selectedTab === SpecialsGalleryTabEnum.BUNDLES)
                      return (
                        <BundleCard
                          key={bundle.id}
                          comedian={comedian}
                          bundle={{
                            ...bundle,
                            unlocked: userPaidCollectionIds.includes(bundle.id),
                            price: bundlePrices[bundle.id] || 0,
                          }}
                          width={411}
                          height={236}
                          mobileWidth={165}
                          mobileHeight={165}
                          useSquareImgOnMobile
                          mobileClassName="flex flex-row justify-items-center gap-x-5 w-full"
                          location="specialsGalleryBundles"
                        />
                      );
                  })}
          </div>
          {bundlesToDisplay && displayCount < bundlesToDisplay.length && (
            <button
              className="py-4 w-full mt-4 border btn-outlined border-slate-gray-900 text-slate-gray-900 md:w-[unset]"
              onClick={handleSeeMoreBundles}
            >
              See more
            </button>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
